// ─── Expression Compiler ──────────────────────────────────────────────────────
//
// Platform primitive: safe recursive descent expression compiler.
// Stateless, zero runtime dependency. Owns grammar definition and safety policy.
//
// Extracted from stateHydration.ts (Phase 0). All consumers that previously
// imported from @/state/stateHydration continue to work; stateHydration
// re-exports from here. New code imports directly from this module.
//
// Supported grammar:
//
//   expr     = ternary
//   ternary  = or ('?' expr ':' expr)?
//   or       = and ('||' and)*
//   and      = not ('&&' not)*
//   not      = '!' not | compare
//   compare  = add (('===' | '!==' | '>=' | '<=' | '>' | '<') add)?
//   add      = mul (('+' | '-') mul)*
//   mul      = unary (('*' | '/') unary)*
//   unary    = '-' unary | primary
//   primary  = number | string_literal | 'true' | 'false' | 'null'
//            | 'deps' ('[' string_literal ']' | '.' identifier)
//            | '(' expr ')'
//
// Prohibited: function calls, property chains beyond deps.*, assignment,
// typeof, instanceof, new, eval, spread, await, yield, and any identifier
// that is not a reserved literal (true/false/null) or deps.

import type { StateValue } from '@/core/schema/stateSchema';

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Compile a schema expression string into a safe compute function.
 * The returned function accepts a deps map and returns a StateValue.
 *
 * Throws ExpressionCompileError if the expression uses forbidden constructs
 * or contains syntax the evaluator cannot parse.
 */
export function compileExpression(
  expression: string,
): (deps: Record<string, StateValue>) => StateValue {
  // Pre-validate for forbidden tokens before parsing
  validateNoForbiddenTokens(expression);

  // Parse once at compile time to catch errors early
  const tokens = tokenize(expression);
  // Attempt a dry-run parse with an empty deps to verify structure
  try {
    const parser = new Parser(tokens);
    parser.parseExpr();
    if (!parser.isAtEnd()) {
      throw new ExpressionCompileError(`Unexpected token after expression end`);
    }
  } catch (e) {
    if (e instanceof ExpressionCompileError) throw e;
    throw new ExpressionCompileError(`Parse error: ${e instanceof Error ? e.message : String(e)}`);
  }

  return (deps: Record<string, StateValue>): StateValue => {
    try {
      const toks = tokenize(expression);
      const parser = new Parser(toks, deps);
      return parser.parseExpr();
    } catch {
      return null;
    }
  };
}

export class ExpressionCompileError extends Error {
  constructor(message: string) {
    super(`[ExpressionCompileError] ${message}`);
    this.name = 'ExpressionCompileError';
  }
}

// ─── Tokenizer ────────────────────────────────────────────────────────────────

type TokenKind =
  | 'NUMBER' | 'STRING' | 'BOOL' | 'NULL'
  | 'DEPS'
  | 'DOT' | 'LBRACKET' | 'RBRACKET'
  | 'LPAREN' | 'RPAREN'
  | 'PLUS' | 'MINUS' | 'STAR' | 'SLASH'
  | 'BANG'
  | 'EQ3' | 'NEQ3' | 'GTE' | 'LTE' | 'GT' | 'LT'
  | 'AND' | 'OR'
  | 'QUESTION' | 'COLON'
  | 'IDENT'
  | 'EOF';

interface Token {
  kind: TokenKind;
  value: string;
}

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  const peek = () => src[i] ?? '';
  const advance = () => src[i++] ?? '';

  while (i < src.length) {
    const ch = peek();

    // Whitespace
    if (/\s/.test(ch)) { advance(); continue; }

    // Number
    if (/[0-9]/.test(ch) || (ch === '-' && /[0-9]/.test(src[i + 1] ?? ''))) {
      let num = advance();
      while (/[0-9.]/.test(peek())) num += advance();
      tokens.push({ kind: 'NUMBER', value: num });
      continue;
    }

    // String literal (single or double quotes)
    if (ch === '"' || ch === "'") {
      const quote = advance();
      let str = '';
      while (peek() !== quote && i < src.length) str += advance();
      advance(); // closing quote
      tokens.push({ kind: 'STRING', value: str });
      continue;
    }

    // Identifiers and keywords
    if (/[a-zA-Z_$]/.test(ch)) {
      let id = '';
      while (/[a-zA-Z0-9_$]/.test(peek())) id += advance();
      if (id === 'true' || id === 'false') {
        tokens.push({ kind: 'BOOL', value: id });
      } else if (id === 'null') {
        tokens.push({ kind: 'NULL', value: id });
      } else if (id === 'deps') {
        tokens.push({ kind: 'DEPS', value: id });
      } else {
        tokens.push({ kind: 'IDENT', value: id });
      }
      continue;
    }

    // Two-char operators
    if (ch === '=' && src[i + 1] === '=' && src[i + 2] === '=') {
      i += 3; tokens.push({ kind: 'EQ3', value: '===' }); continue;
    }
    if (ch === '!' && src[i + 1] === '=' && src[i + 2] === '=') {
      i += 3; tokens.push({ kind: 'NEQ3', value: '!==' }); continue;
    }
    if (ch === '>' && src[i + 1] === '=') {
      i += 2; tokens.push({ kind: 'GTE', value: '>=' }); continue;
    }
    if (ch === '<' && src[i + 1] === '=') {
      i += 2; tokens.push({ kind: 'LTE', value: '<=' }); continue;
    }
    if (ch === '&' && src[i + 1] === '&') {
      i += 2; tokens.push({ kind: 'AND', value: '&&' }); continue;
    }
    if (ch === '|' && src[i + 1] === '|') {
      i += 2; tokens.push({ kind: 'OR', value: '||' }); continue;
    }

    // Single-char operators
    switch (ch) {
      case '+': advance(); tokens.push({ kind: 'PLUS',     value: '+' }); break;
      case '-': advance(); tokens.push({ kind: 'MINUS',    value: '-' }); break;
      case '*': advance(); tokens.push({ kind: 'STAR',     value: '*' }); break;
      case '/': advance(); tokens.push({ kind: 'SLASH',    value: '/' }); break;
      case '!': advance(); tokens.push({ kind: 'BANG',     value: '!' }); break;
      case '>': advance(); tokens.push({ kind: 'GT',       value: '>' }); break;
      case '<': advance(); tokens.push({ kind: 'LT',       value: '<' }); break;
      case '?': advance(); tokens.push({ kind: 'QUESTION', value: '?' }); break;
      case ':': advance(); tokens.push({ kind: 'COLON',    value: ':' }); break;
      case '.': advance(); tokens.push({ kind: 'DOT',      value: '.' }); break;
      case '[': advance(); tokens.push({ kind: 'LBRACKET', value: '[' }); break;
      case ']': advance(); tokens.push({ kind: 'RBRACKET', value: ']' }); break;
      case '(': advance(); tokens.push({ kind: 'LPAREN',   value: '(' }); break;
      case ')': advance(); tokens.push({ kind: 'RPAREN',   value: ')' }); break;
      default:
        throw new ExpressionCompileError(`Unexpected character: "${ch}"`);
    }
  }

  tokens.push({ kind: 'EOF', value: '' });
  return tokens;
}

// ─── Forbidden token pre-validator ───────────────────────────────────────────

const FORBIDDEN_PATTERNS = [
  /\beval\s*\(/,
  /\bFunction\s*\(/,
  /\bnew\b/,
  /\btypeof\b/,
  /\binstanceof\b/,
  /\bawait\b/,
  /\byield\b/,
  /\bdelete\b/,
  /\bvoid\b/,
  /\bthrow\b/,
  /\breturn\b/,
  // NOTE: assignment (=) is not checked here because the tokenizer already throws
  // ExpressionCompileError on any bare = that isn't part of ===, !==, >=, <=.
  /`/,         // template literals
  /\.\.\./,    // spread
];

function validateNoForbiddenTokens(expression: string): void {
  for (const pattern of FORBIDDEN_PATTERNS) {
    if (pattern.test(expression)) {
      throw new ExpressionCompileError(
        `Expression contains forbidden construct matching ${pattern.source}`
      );
    }
  }
}

// ─── Recursive Descent Parser ─────────────────────────────────────────────────

class Parser {
  private pos = 0;

  constructor(
    private tokens: Token[],
    private deps: Record<string, StateValue> = {},
  ) {}

  isAtEnd(): boolean {
    return this.tokens[this.pos]?.kind === 'EOF';
  }

  parseExpr(): StateValue {
    return this.parseTernary();
  }

  private peek(): Token {
    return this.tokens[this.pos] ?? { kind: 'EOF', value: '' };
  }

  private advance(): Token {
    return this.tokens[this.pos++] ?? { kind: 'EOF', value: '' };
  }

  private match(...kinds: TokenKind[]): boolean {
    if (kinds.includes(this.peek().kind)) {
      this.advance();
      return true;
    }
    return false;
  }

  private expect(kind: TokenKind): Token {
    if (this.peek().kind !== kind) {
      throw new ExpressionCompileError(
        `Expected ${kind} but got ${this.peek().kind} ("${this.peek().value}")`
      );
    }
    return this.advance();
  }

  private parseTernary(): StateValue {
    const cond = this.parseOr();
    if (this.peek().kind === 'QUESTION') {
      this.advance();
      const then = this.parseExpr();
      this.expect('COLON');
      const else_ = this.parseExpr();
      return cond ? then : else_;
    }
    return cond;
  }

  private parseOr(): StateValue {
    let left = this.parseAnd();
    while (this.peek().kind === 'OR') {
      this.advance();
      const right = this.parseAnd();
      left = (left || right) as StateValue;
    }
    return left;
  }

  private parseAnd(): StateValue {
    let left = this.parseNot();
    while (this.peek().kind === 'AND') {
      this.advance();
      const right = this.parseNot();
      left = (left && right) as StateValue;
    }
    return left;
  }

  private parseNot(): StateValue {
    if (this.peek().kind === 'BANG') {
      this.advance();
      return !this.parseNot() as StateValue;
    }
    return this.parseCompare();
  }

  private parseCompare(): StateValue {
    const left = this.parseAdd();
    const op = this.peek().kind;
    if (['EQ3', 'NEQ3', 'GTE', 'LTE', 'GT', 'LT'].includes(op)) {
      this.advance();
      const right = this.parseAdd();
      switch (op) {
        case 'EQ3':  return (left === right) as StateValue;
        case 'NEQ3': return (left !== right) as StateValue;
        case 'GTE':  return ((left as number) >= (right as number)) as StateValue;
        case 'LTE':  return ((left as number) <= (right as number)) as StateValue;
        case 'GT':   return ((left as number) >  (right as number)) as StateValue;
        case 'LT':   return ((left as number) <  (right as number)) as StateValue;
      }
    }
    return left;
  }

  private parseAdd(): StateValue {
    let left = this.parseMul();
    while (this.peek().kind === 'PLUS' || this.peek().kind === 'MINUS') {
      const op = this.advance().kind;
      const right = this.parseMul();
      if (op === 'PLUS') {
        left = ((left as number) + (right as number)) as StateValue;
      } else {
        left = ((left as number) - (right as number)) as StateValue;
      }
    }
    return left;
  }

  private parseMul(): StateValue {
    let left = this.parseUnary();
    while (this.peek().kind === 'STAR' || this.peek().kind === 'SLASH') {
      const op = this.advance().kind;
      const right = this.parseUnary();
      if (op === 'STAR') {
        left = ((left as number) * (right as number)) as StateValue;
      } else {
        const r = right as number;
        left = (r === 0 ? null : (left as number) / r) as StateValue;
      }
    }
    return left;
  }

  private parseUnary(): StateValue {
    if (this.peek().kind === 'MINUS') {
      this.advance();
      return -(this.parseUnary() as number) as StateValue;
    }
    return this.parsePrimary();
  }

  private parsePrimary(): StateValue {
    const tok = this.peek();

    if (tok.kind === 'NUMBER') {
      this.advance();
      return parseFloat(tok.value) as StateValue;
    }

    if (tok.kind === 'STRING') {
      this.advance();
      return tok.value as StateValue;
    }

    if (tok.kind === 'BOOL') {
      this.advance();
      return (tok.value === 'true') as StateValue;
    }

    if (tok.kind === 'NULL') {
      this.advance();
      return null;
    }

    if (tok.kind === 'DEPS') {
      this.advance();
      return this.parseDepsAccess();
    }

    if (tok.kind === 'LPAREN') {
      this.advance();
      const val = this.parseExpr();
      this.expect('RPAREN');
      return val;
    }

    throw new ExpressionCompileError(
      `Unexpected token: ${tok.kind} ("${tok.value}")`
    );
  }

  private parseDepsAccess(): StateValue {
    if (this.peek().kind === 'DOT') {
      this.advance();
      const key = this.expect('IDENT').value;
      return this.deps[key] ?? null;
    }

    if (this.peek().kind === 'LBRACKET') {
      this.advance();
      const keyTok = this.expect('STRING');
      this.expect('RBRACKET');
      return this.deps[keyTok.value] ?? null;
    }

    throw new ExpressionCompileError(
      `Expected "." or "[" after "deps", got ${this.peek().kind}`
    );
  }
}
